import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
    DeleteRecord,
    GetListview,
    SuccessAlert,
    URLPRIFIX,
    ErrorAlert,
    GetStatusCounts,
    GetRecords,
    UpdateRecord
} from "../../functions/api";
import { useStore } from "../../Components/StateProvider";
import Pagination from "../../Components/Fields/Pagination";
import { getlocal } from "../../functions/localstorage";
import StatusTabs from "../../Components/Elements/StatusTabs";
import ListViewUp from "../../Components/Elements/ListViewUp";
import BulkActionsBar from "../../Components/Elements/BulkActionsBar";
import BulkUpdateModal from "../../Components/Elements/BulkUpdateModal";

const Task_Assigned = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const user = getlocal("system")?.user || {};
    const { id } = useParams();

    const [activeTab, setActiveTab] = useState("ALL");
    const [selectedRecords, setSelectedRecords] = useState([]);
    const [statusTabs, setStatusTabs] = useState([]);
    const [users, setUsers] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [hasTabClicked, setHasTabClicked] = useState(false);
    const [filters, setFilters] = useState({});
    const [sortConfig, setSortConfig] = useState({
        field: "createdDate",
        direction: "desc",
    });

    const tablename = "tasks";
    // Fixed tableURL to include the project/type ID correctly
    const tableURL = "/subtask/getsubtasklistbyType/11"; 
    const DeleteURL = "/subtask/deletesubtask/";
    const statusCountURL = `/subtask/getprojectsubtaskstatuscount/11/${user.id}`;

    // Helper to get the correct URL based on user role
    const getDynamicTableURL = useCallback(() => {
        // If an 'id' param exists from the URL, use it, otherwise use user.id
        const targetId = id || user.id;
        return user.role === 1 ? `${tableURL}/${targetId}` : `${tableURL}/${targetId}`;
    }, [user.role, user.id, tableURL, id]);

    const Tablehead = " pr-[10px] max-sm:text-[26px] md:text-[20px] lg:text-[16px] xl:text-[20px] font-bold ";
    const Tablerecord = " pr-[10px] max-sm:text-[26px] md:text-[20px] lg:text-[12px] xl:text-[16px] overflow-hidden";

    const Header = [
        ["Task Title", "subtask_title"],
        ["Project Name", "projectName"],
        ["Document Name", "task_title"],
        ["Client", "clientname"],
        ["Assigned To", "assigned_to_username"],
        ["Status", "status"],
        ["Due Date", "dueDate"],
        ["Action", "ACTION"],
    ];

    const store = useStore();
    const init = {
        filterfield: "all",
        search: "",
        sort: "desc",
        currentpage: 1,
        totalpage: 1,
        pagesize: 20,
    };

    const loadListview = useCallback((params = {}) => {
        GetListview({
            listUrl: getDynamicTableURL(),
            store,
            // Unified these keys to ensure they match what 'change' sets
            field: params.field ?? store.getvalue("taskfilterfield") ?? init.filterfield,
            search: params.search ?? store.getvalue("tasksearch") ?? init.search,
            sort: params.sort ?? store.getvalue("tasksort") ?? init.sort,
            page: params.page ?? store.getvalue("taskCurrentPage") ?? init.currentpage,
            pagesize: params.pagesize ?? store.getvalue("taskpagesize") ?? init.pagesize,
        });
    }, [store, getDynamicTableURL]);

    const fetchStatusCounts = useCallback(async () => {
        try {
            const counts = await GetStatusCounts(statusCountURL, store);
            if (counts?.statuscount) {
                setStatusTabs(counts.statuscount);
            }
        } catch {
            ErrorAlert("Failed to fetch status counts.");
        }
    }, [statusCountURL, store]);

    const change = useCallback((props) => {
        const field = props.field || "all";
        const search = props.search || "";
        const pagesize = props.pagesize || store.getvalue("taskpagesize") || init.pagesize;
        const sort = props.sort || init.sort;
        const page = props.page || 1;

        // Synchronize state with store for API calls
        store.setvalue("taskfilterfield", field);
        store.setvalue("tasksearch", search);
        store.setvalue("tasksort", sort);
        store.setvalue("taskpagesize", pagesize);
        store.setvalue("taskCurrentPage", page);

        loadListview({ field, search, sort, page, pagesize });
    }, [store, loadListview]);

    useEffect(() => {
        store.setmodule("nonlitigation_tasks", init).then(() => {
            fetchStatusCounts();
            store.setheader(`${tablename} List`);
            store.stopLoading();

            const storedField = queryParams.get("field") || store.getvalue("taskfilterfield") || "all";
            const storedSearch = queryParams.get("search") || store.getvalue("tasksearch") || "";
            const storedTab = store.getvalue("taskActiveTab") || "ALL";

            setActiveTab(storedTab);
            setHasTabClicked(true);

            // Re-apply filter state to UI filters
            if (storedField !== "all") {
                setFilters({ [storedField]: storedSearch });
            }

            change({
                field: storedField,
                search: storedSearch,
                page: store.getvalue("taskCurrentPage") || 1,
                pagesize: store.getvalue("taskpagesize") || 20,
            });
        });
    }, []);

    const onpage = (page) => {
        change({
            field: store.getvalue("taskfilterfield"),
            search: store.getvalue("tasksearch"),
            sort: store.getvalue("tasksort"),
            page: page,
            pagesize: store.getvalue("taskpagesize")
        });
    };
    const fetchUsers = useCallback(async () => {
        try {
            const response = await GetRecords("/users/getusers?fieldname=all&sort=asc&page=1&count=10&pagesize=1000");
            if (response?.record?.list?.length) {
                setUsers(response.record.list);
            }
        } catch { }
    }, []);

    const handleBulkAssign = useCallback(() => {
        setIsModalOpen(true);
        fetchUsers();
    }, [fetchUsers]);

    const handleBulkAssignUpdate = useCallback(async (data) => {
        const dueDateIso = data.dueDate ? new Date(data.dueDate).toISOString() : null;
        store.startLoading();
        try {
            const taskIds = selectedRecords.map((item) => item.id);
            const payload = {
                task_ids: taskIds,
                assigned_to: data.assignedTo,
                modified_by: store.getuser().id,
                ...(data.dueDate && { due_date: dueDateIso }),
            };

            const res = await UpdateRecord("/subtask/bulkupdatetasks/11", payload);
            if (res?.success) {
                SuccessAlert("Selected items assigned successfully!");
            } else {
                ErrorAlert(res?.errorMessage || "Bulk update failed.");
            }
        } catch (error) {
            ErrorAlert(`Bulk update failed: ${error.message}`);
        } finally {
            setIsModalOpen(false);
            setSelectedRecords([]);
            loadListview({ ...init });
            store.stopLoading();
        }
    }, [selectedRecords, store, loadListview]);

    const handleBulkApprove = async () => {
        if (selectedRecords.length === 0) return;
        store.startLoading();
        try {
            const payload = {
                subtask_ids: selectedRecords.map(i => i.id),
                modified_by: store.getuser().id,
            };
            const res = await UpdateRecord(`/subtask/bulkapprove/${store.getuser().id}`, payload);
            if (res?.success) SuccessAlert("Tasks approved successfully!");
            else ErrorAlert(res?.errorMessage || "Approval failed.");
        } catch (error) {
            ErrorAlert(error.message);
        } finally {
            fetchStatusCounts();
            loadListview();
            setSelectedRecords([]);
            store.stopLoading();
        }
    };

    const handleBulkNotify = async () => {
        if (selectedRecords.length === 0) return;
        store.startLoading();
        try {
            const payload = {
                subtask_ids: selectedRecords.map(i => i.id),
                modified_by: store.getuser().id,
            };
            const res = await UpdateRecord("/subtask/bulknotificationsend", payload);
            if (res?.success) SuccessAlert(res.message);
            else ErrorAlert(res?.errorMessage || "Notification failed.");
        } catch (error) {
            ErrorAlert(error.message);
        } finally {
            loadListview();
            setSelectedRecords([]);
            store.stopLoading();
        }
    };

    const handleBulkDelete = async () => {
        if (!window.confirm(`Delete ${selectedRecords.length} items?`)) return;
        store.startLoading();
        let allSuccess = true;
        for (const item of selectedRecords) {
            const res = await DeleteRecord(DeleteURL + item.id, { modifiedBy: store.getuser().id });
            if (!res.success) allSuccess = false;
        }
        if (allSuccess) SuccessAlert("Deleted successfully!");
        setSelectedRecords([]);
        loadListview();
        fetchStatusCounts();
        store.stopLoading();
    };

    const handleDelete = useCallback(async (item) => {
        if (!window.confirm("Are you sure?")) return;
        store.startLoading();
        try {
            const res = await DeleteRecord(`${DeleteURL}${item.id}`, { modifiedBy: store.getuser().id });
            if (res.success) {
                SuccessAlert("Deleted Successfully");
                loadListview();
                fetchStatusCounts();
            }
        } catch (error) {
            ErrorAlert(error.message);
        } finally {
            store.stopLoading();
        }
    }, [loadListview, fetchStatusCounts]);

    

    const handleFilterChange = useCallback((label, value) => {
        const updatedFilters = { ...filters, [label]: value };
        setFilters(updatedFilters);
        // Save filter to store for persistence
        store.setvalue("taskFilters", updatedFilters);
        
        change({
            field: label,
            search: value,
            sort: sortConfig.direction,
        });
    }, [filters, sortConfig, change, store]);

    const handleOnSelect = (itemOrItems, isChecked) => {
        if (Array.isArray(itemOrItems)) {
            setSelectedRecords(isChecked ? [...itemOrItems] : []);
        } else {
            if (isChecked) {
                setSelectedRecords((prev) => [...prev, itemOrItems]);
            } else {
                setSelectedRecords((prev) => prev.filter((r) => r.id !== itemOrItems.id));
            }
        }
    };

    // ... (fetchUsers, handleBulkAssign, handleBulkAssignUpdate remain the same) ...

    const handleSort = useCallback((label) => {
        const direction = sortConfig.field === label && sortConfig.direction === "asc" ? "desc" : "asc";
        setSortConfig({ field: label, direction });
        change({
            field: store.getvalue("taskfilterfield"),
            search: store.getvalue("tasksearch"),
            sort: direction,
            page: 1, // Reset to page 1 on sort
        });
    }, [sortConfig, change, store]);

    const handleTabClick = useCallback((status, count) => {
        setActiveTab(status);
        setHasTabClicked(true);
        store.setvalue("taskActiveTab", status);
        
        const field = "status";
        const search = status.toUpperCase() === "ALL" ? "" : status;
        
        setFilters(status.toUpperCase() === "ALL" ? {} : { [field]: search });
        
        change({ 
            field: status.toUpperCase() === "ALL" ? "all" : field, 
            search, 
            page: 1, 
            pagesize: count || store.getvalue("taskpagesize") || init.pagesize 
        });
    }, [store, change, init.pagesize]);

    const handleClearFilter = useCallback(() => {
        setActiveTab("ALL");
        setFilters({});
        setHasTabClicked(false);
        store.setvalue("taskFilters", {});
        store.setvalue("taskActiveTab", "ALL");
        change({ ...init });
    }, [change, store, init]);

    // Local filtering for records already in the list
    const records = store.getvalue("list") || [];
    const filteredRecords = records.filter((record) => {
        const upper = activeTab.toUpperCase();
        if (upper !== "ALL" && record.status?.toUpperCase() !== upper) return false;

        return Header.every(([_, label]) => {
            const filterValue = filters[label];
            if (!filterValue) return true;
            let fieldValue = record[label];
            if (label.toLowerCase().includes("date") && fieldValue) {
                const parsed = new Date(fieldValue);
                if (!isNaN(parsed)) fieldValue = parsed.toISOString().split("T")[0];
            }
            return fieldValue?.toString().toLowerCase().includes(filterValue.toLowerCase());
        });
    });

    return (
        <div className="grid grid-rows-10 h-full">
            <div className="flex flex-wrap justify-between items-center mb-2 px-2">
                <div className="flex-1 overflow-x-auto">
                    <StatusTabs
                        statusTabs={statusTabs}
                        activeTab={activeTab}
                        onTabClick={handleTabClick}
                        hasTabClicked={hasTabClicked}
                        onClearClick={handleClearFilter}
                    />
                </div>
                {store.getprops("add") && (
                    <button
                        className="bg-primary text-secondary px-3 py-1 rounded-full border-2 transition text-sm whitespace-nowrap"
                        onClick={() => {
                            store.removemodule("nonlitigation_tasks");
                            store.navTo("/demo/" + tablename + "/create");
                        }}
                    >
                        ➕ ADD NEW
                    </button>
                )}
            </div>

            <div className="row-span-9 h-full w-full">
                <div className="py-5 px-[30px] h-full w-full">
                    <ListViewUp
                        header={Header}
                        filteredRecords={filteredRecords}
                        Tablehead={Tablehead}
                        Tablerecord={Tablerecord}
                        sortConfig={sortConfig}
                        handleSort={handleSort}
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        store={store}
                        handleOnSelect={handleOnSelect}
                        isSelected={(item) => selectedRecords.some((s) => s.id === item.id)}
                        handleClickRow={(item) => store.getprops("view") && store.navTo(`/${URLPRIFIX}/${tablename}/view/${item.id}`)}
                        handleDelete={handleDelete}
                        handleSelectAll={(e) => handleOnSelect(filteredRecords, e.target.checked)}
                        isAllSelected={filteredRecords.length > 0 && selectedRecords.length === filteredRecords.length}
                        tablename={tablename}
                    />
                    <BulkActionsBar
                        selectedRecords={selectedRecords}
                        store={store}
                        onBulkAssign={handleBulkAssign}
                        onBulkDelete={handleBulkDelete}
                        onBulkApprove={handleBulkApprove}
                        onBulkNotify={handleBulkNotify}
                    />
                    <BulkUpdateModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        onUpdate={handleBulkAssignUpdate}
                        users={users}
                    />
                    <div className="float-right mr-8 mt-6">
                        <Pagination onchange={onpage} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Task_Assigned;